import { useEffect } from "react";
import { NoData } from "@/components/NoData";
import { Spinner } from "@/components/Spinner";
import { DEFAULT_STATE_VALUES } from "@/shared/constants/common.ts";
import {
	formatValueCurrency,
	formatValueDate,
	formatValueNumber,
	formatValuePercent,
} from "../helpers/FormattingHelpers.ts";
import { HoldersTable } from "./HoldersTable.tsx";
import type { HoldersState } from "./type.ts";
import { UseData } from "./UseData.ts";

export const Holders = ({ state }: { state: HoldersState }) => {
	const ticker = state?.ticker ?? DEFAULT_STATE_VALUES.ticker;
	const {
		isLoading: loading,
		data: holdersInfo,
		error,
		execute,
	} = UseData.create().useGetHoldersInfoData();

	useEffect(() => {
		if (ticker) {
			execute({
				params: {
					sort: [{ name: "date", order: -1 }],
					pagination: { limit: 1, offset: 0 },
				},
				ticker,
			});
		}
	}, [ticker, execute]);

	return (
		<>
			{loading ? (
				<Spinner />
			) : error || !holdersInfo ? (
				<NoData />
			) : (
				<div className="h-full flex flex-col overflow-auto">
					<div className="p-2">
						<h2 className="text-lg font-semibold mb-4">{ticker}</h2>
						<div
							className="grid grid-cols-2 gap-8 mb-8"
							style={{
								gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
							}}
						>
							<div className="space-y-2">
								<div className="flex justify-between">
									<span className="text-gold">Date:</span>
									<span>{formatValueDate(holdersInfo.date, "N/A")}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gold">Growth:</span>
									<span>{formatValuePercent(holdersInfo.growth, "N/A")}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gold">Net:</span>
									<span>{formatValueCurrency(holdersInfo.net, "N/A")}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gold">Institutional Score:</span>
									<span>
										{formatValueNumber(holdersInfo.institutionalScore, "N/A")}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gold">Holders Number:</span>
									<span>
										{formatValueNumber(holdersInfo.holdersNumber, "N/A")}
									</span>
								</div>
							</div>

							<div className="space-y-2">
								<div className="flex justify-between">
									<span className="text-gold">Increase:</span>
									<span>{formatValuePercent(holdersInfo.increase, "N/A")}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gold">Decrease:</span>
									<span>{formatValuePercent(holdersInfo.decrease, "N/A")}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gold">Unchange:</span>
									<span>{formatValuePercent(holdersInfo.unchange, "N/A")}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gold">Value:</span>
									<span>{formatValueNumber(holdersInfo.value, "N/A")}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gold">Amount:</span>
									<span>{formatValueCurrency(holdersInfo.amount, "N/A")}</span>
								</div>
							</div>
						</div>
					</div>
					<div className="flex-1">
						<HoldersTable ticker={ticker} />
					</div>
				</div>
			)}
		</>
	);
};
